import { sortVariants } from '@/lib/variant-sort';
import { CURRENCY_HEADER, SKU_DEFAULT_COST_HEADER } from './skuBulkEditCsv';

/**
 * Products > Export CSV: the rows of the Products export, which is also the
 * Bulk edit CSV template (`skuBulkEditCsv.ts` reads it back).
 *
 * PURE. No `t()`, no React, no tRPC; the page fetches every page and
 * downloads the file.
 *
 * One cost column: SKU Default Cost (the SKU's own cost, 2 decimals, blank
 * when unset), then Currency: the currency that cost was typed in, else the
 * org currency. It is on EVERY SKU row, including rows whose cost is blank,
 * so whoever types a new cost sees its currency. It is blank on product-only
 * rows, and on every row when the caller passes `''` (a 3PL viewing a client
 * catalog: the caller's currency is not the client's, and client rows carry
 * a null cost currency). Bulk edit reads Currency back, so an untouched
 * export uploads as no changes.
 */

export const PRODUCTS_EXPORT_HEADERS = [
  'Handle',
  'Title',
  'Product Type',
  'Production Type',
  'Tags',
  'Published',
  'Image Src',
  'Status',
  'Variant SKU',
  'Variant Title',
  'Option1 Name',
  'Option1 Value',
  'Option2 Name',
  'Option2 Value',
  'Variant Image',
  SKU_DEFAULT_COST_HEADER,
  CURRENCY_HEADER,
  'Variant Weight',
  'Variant Weight Unit',
  'HS Code',
] as const;

type Header = (typeof PRODUCTS_EXPORT_HEADERS)[number];
type ExportCell = string | number;

/** The SKU fields the export writes (a product variant or an ungrouped SKU). */
export interface ProductsExportSku {
  sku: string;
  title: string | null;
  size: string | null;
  color: string | null;
  imageUrl: string | null;
  skuCostOverride: number | null;
  /** The Default cost's currency; null or absent = the org currency. */
  skuCostOverrideCurrency?: string | null;
  weightValue: number | null;
  weightUnit: string | null;
  hsCode: string | null;
}

export interface ProductsExportProduct {
  handle: string | null;
  title: string | null;
  productType: string | null;
  productionType: string | null;
  tags: string[] | null;
  status: string | null;
  imageUrl: string | null;
  variants: ProductsExportSku[];
}

export interface ProductsExportUngroupedSku extends ProductsExportSku {
  category: string | null;
  productionType: string | null;
}

const formatCost = (value: number | null | undefined): string => (value != null ? value.toFixed(2) : '');

/** Emits a row in header order; any header not given is blank. */
const toRow = (cells: Partial<Record<Header, ExportCell>>): ExportCell[] =>
  PRODUCTS_EXPORT_HEADERS.map((header) => cells[header] ?? '');

const productCells = (product: ProductsExportProduct): Partial<Record<Header, ExportCell>> => ({
  Handle: product.handle ?? '',
  Title: product.title ?? '',
  'Product Type': product.productType ?? '',
  'Production Type': product.productionType ?? '',
  Tags: (product.tags ?? []).join(', '),
  Published: product.status === 'active' ? 'TRUE' : 'FALSE',
  'Image Src': product.imageUrl ?? '',
  Status: product.status ?? '',
});

const skuCells = (sku: ProductsExportSku, currency: string): Partial<Record<Header, ExportCell>> => ({
  'Variant SKU': sku.sku,
  'Variant Title': sku.title ?? '',
  'Option1 Name': sku.size ? 'Size' : '',
  'Option1 Value': sku.size ?? '',
  'Option2 Name': sku.color ? 'Color' : '',
  'Option2 Value': sku.color ?? '',
  'Variant Image': sku.imageUrl ?? '',
  [SKU_DEFAULT_COST_HEADER]: formatCost(sku.skuCostOverride),
  [CURRENCY_HEADER]: sku.skuCostOverrideCurrency ?? currency,
  'Variant Weight': sku.weightValue != null ? sku.weightValue : '',
  'Variant Weight Unit': sku.weightUnit ?? '',
  'HS Code': sku.hsCode ?? '',
});

/** The header row, then one row per variant (or one per variant-less product), then the ungrouped SKUs. */
export function buildProductsExportRows({
  products,
  ungroupedSkus,
  currency,
}: {
  products: readonly ProductsExportProduct[];
  ungroupedSkus: readonly ProductsExportUngroupedSku[];
  /** The org currency code; `''` in client view. */
  currency: string;
}): ExportCell[][] {
  const rows: ExportCell[][] = [[...PRODUCTS_EXPORT_HEADERS]];

  for (const product of products) {
    const sorted = sortVariants(product.variants);
    if (sorted.length === 0) {
      rows.push(toRow(productCells(product)));
      continue;
    }
    for (const variant of sorted) {
      rows.push(toRow({ ...productCells(product), ...skuCells(variant, currency) }));
    }
  }

  for (const sku of ungroupedSkus) {
    rows.push(
      toRow({
        'Product Type': sku.category ?? '',
        'Production Type': sku.productionType ?? '',
        ...skuCells(sku, currency),
      }),
    );
  }

  return rows;
}
